import React, { useMemo } from 'react';
import { IMaskInput } from 'react-imask';
import { FaSpinner } from 'react-icons/fa6';
import { TextInput } from './Inputs/TextInput';
import { BRAZILIAN_STATES } from '../Constants/brazilianStates';
import { CHECKOUT_FIELD_LIMITS } from '../Constants/checkoutFieldLimits';
import { CardHolderFieldsProps } from '../Types';
import { UfMunicipioSearchFields } from './UfMunicipioSearchFields';

export const CardHolderFields: React.FC<CardHolderFieldsProps> = ({
  values,
  errors,
  onChange,
  onCepBlur,
  loadingCep,
  cityOptions,
  loadingCities,
}) => {
  const estados = useMemo(
    () => BRAZILIAN_STATES.map((s) => ({ sigla: s.uf, nome: s.name })),
    []
  );

  return (
    <div className="flex flex-col gap-4 mt-4">
      {/* CEP (pequeno) | Endereço | Número (pequeno) */}
      <div className="flex flex-col md:flex-row gap-4 items-start">
        <div className="w-full md:w-36 shrink-0">
          <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
            CEP <span className="text-red-500">*</span>
            {loadingCep && <FaSpinner className="animate-spin w-3 h-3 text-sky-500" />}
          </label>
          <IMaskInput
            mask="00000-000"
            value={values.holderCep}
            onAccept={(val: string) => onChange('holderCep', val)}
            onBlur={onCepBlur}
            placeholder="00000-000"
            className={`w-full px-4 mt-1 py-2 h-[42px] rounded-lg border bg-white ${
              errors.holderCep ? 'border-red-500' : 'border-gray-300'
            } focus:outline-none focus:ring-1 focus:ring-blue-300`}
          />
          {errors.holderCep && <p className="text-red-500 text-sm mt-1">{errors.holderCep}</p>}
        </div>

        <div className="w-full md:flex-1 min-w-0">
          <TextInput
            name="holderAddress"
            label="Endereço"
            value={values.holderAddress}
            onChange={(e) => onChange('holderAddress', e.target.value)}
            errorMessage={errors.holderAddress}
            placeholder="Rua, avenida..."
            requiredMark
            maxLength={CHECKOUT_FIELD_LIMITS.holderAddress}
            labelSize="sm"
            labelColor="gray-600"
            labelWeight="medium"
          />
        </div>

        <div className="w-full md:w-28 shrink-0">
          <TextInput
            name="holderNumber"
            label="Número"
            value={values.holderNumber}
            onChange={(e) => onChange('holderNumber', e.target.value)}
            errorMessage={errors.holderNumber}
            placeholder="Nº"
            requiredMark
            maxLength={CHECKOUT_FIELD_LIMITS.holderNumber}
            labelSize="sm"
            labelColor="gray-600"
            labelWeight="medium"
          />
        </div>
      </div>

      {/* Bairro | Estado | Município */}
      <div className="flex flex-col md:flex-row gap-4 items-start">
        <div className="w-full md:flex-1 min-w-0">
          <TextInput
            name="holderNeighborhood"
            label="Bairro"
            value={values.holderNeighborhood}
            onChange={(e) => onChange('holderNeighborhood', e.target.value)}
            errorMessage={errors.holderNeighborhood}
            placeholder="Bairro"
            requiredMark
            maxLength={CHECKOUT_FIELD_LIMITS.holderNeighborhood}
            labelSize="sm"
            labelColor="gray-600"
            labelWeight="medium"
          />
        </div>

        <UfMunicipioSearchFields
          estados={estados}
          municipios={cityOptions}
          estado={values.holderState}
          municipio={values.holderCity}
          onEstadoChange={(uf) => onChange('holderState', uf)}
          onMunicipioChange={(city) => onChange('holderCity', city)}
          errors={{ estado: errors.holderState, municipio: errors.holderCity }}
          loadingMunicipios={loadingCities}
        />
      </div>
    </div>
  );
};
